import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * faq — a collapsible Q&A accordion (Shrine collapsible-content parity). Content controls the heading
 * (word-HIGHLIGHT engines 1+2, size, alignment); Behavior controls whether the first item starts open and
 * the open/close ICON style (chevron vs plus/minus); a shared 13-pattern BACKGROUND can sit behind it; the
 * shared "Section" universal group controls scheme / padding / width. Each Q&A is a repeatable `qa` BLOCK
 * (a plain-text question + a richtext answer). Seeds 3 demo Q&A.
 */
export const faqSchema = {
    type: 'faq',
    name: 'FAQ',
    version: 1,
    templates: ['product', 'index', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Questions fréquentes</p>' },
            info: 'Apply Bold (B) to a word to give it the highlight colour — like the Text block.',
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h2',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
            ],
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'Content',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        ...headingHighlightFields(1),
        // ── Behavior ────────────────────────────────────────────────────────────
        {
            id: 'openFirst',
            type: 'checkbox',
            label: 'Open first item',
            group: 'Behavior',
            info: 'The first question starts expanded; others stay collapsed.',
            default: true,
        },
        {
            id: 'iconStyle',
            type: 'select',
            label: 'Toggle icon',
            group: 'Behavior',
            default: 'chevron',
            options: [
                { value: 'chevron', label: 'Chevron' },
                { value: 'plus', label: 'Plus / minus' },
            ],
        },
        // ── Background pattern (shared engine). ──────────────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        {
            // 'simple' = the historical flat accordion (qa blocks directly under the section, untouched).
            // 'categories' = the tabbed FAQ anatomy: `category` child blocks (each holding its own qa
            // children) render as a left tab column inside a surface card; the active category's questions
            // accordion on the right. Flat qa blocks are IGNORED in categories mode (and vice versa).
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Layout',
            default: 'simple',
            options: [
                { value: 'simple', label: 'Simple accordion' },
                { value: 'categories', label: 'Category tabs + accordion' },
            ],
        },
        // ── Footer row ───────────────────────────────────────────────────────────
        {
            id: 'footerText',
            type: 'text',
            label: 'Footer text',
            group: 'Footer',
            default: '',
            info: 'Centered line under the questions (e.g. "Still have questions? We can help."). Empty hides the row.',
            maxLength: 140,
        },
        {
            id: 'footerLinkLabel',
            type: 'text',
            label: 'Footer link label',
            group: 'Footer',
            default: '',
            maxLength: 40,
        },
        {
            id: 'footerLink',
            type: 'link',
            label: 'Footer link',
            group: 'Footer',
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal) ──────────────────────────────────────────────────
        ...universalFields,
    ],
    blocks: [
        {
            type: 'qa',
            name: 'Question',
            max: 24,
            fields: [
                {
                    id: 'question',
                    type: 'text',
                    label: 'Question',
                    default: 'Question',
                    maxLength: 140,
                },
                {
                    id: 'answer',
                    type: 'richtext',
                    label: 'Answer',
                    default: { format: 'html', html: '<p>Réponse…</p>' },
                },
            ],
        },
        {
            // Categories-layout container: one tab in the left column; its qa CHILDREN are the questions
            // shown when the tab is active (2-level nesting, same model as benefit-panel → benefit-row).
            type: 'category',
            name: 'Category',
            max: 8,
            fields: [
                {
                    id: 'label',
                    type: 'text',
                    label: 'Tab label',
                    default: 'Catégorie',
                    defaultKey: 'section.category',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 60,
                },
            ],
            accepts: ['qa'],
        },
    ],
    maxBlocks: 24,
};
export const faqPresets = [
    {
        name: 'FAQ',
        settings: { heading: { format: 'html', html: '<p>Questions fréquentes</p>' }, openFirst: true },
        blocks: [
            {
                type: 'qa',
                settings: {
                    question: '@t:section.faq_q1',
                    answer: {
                        format: 'html',
                        html: '<p>Votre commande est expédiée sous 24–48h. La livraison prend ensuite 2 à 5 jours ouvrés selon votre ville.</p>',
                    },
                },
            },
            {
                type: 'qa',
                settings: {
                    question: '@t:section.faq_q2',
                    answer: {
                        format: 'html',
                        html: '<p>Vous réglez en espèces au livreur, à réception de votre colis. Aucun paiement en ligne n’est requis.</p>',
                    },
                },
            },
            {
                type: 'qa',
                settings: {
                    question: '@t:section.faq_q3',
                    answer: {
                        format: 'html',
                        html: '<p>Oui, vous disposez de 14 jours pour un échange ou un retour. L’article doit être non porté, dans son état d’origine.</p>',
                    },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const faqMeta = { label: 'FAQ', group: 'Product', icon: 'chat' };
//# sourceMappingURL=schema.js.map